/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import {
    object, func, shape, string, arrayOf, number, bool,
} from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router';
import { compose } from 'recompose';
import { useHistory } from 'react-router-dom';
import withPageView from '../../../../helpers/tracking/hocs/withPageView';
import BackButton from '../../common/BackButton';
import { convertToYYYYMMDD, dateMMMMDDYYYY } from '../../utils/dateString';
import ComponentPassport from './Component/Passport';
import { getFeatureFlags } from '../../utils/featureSelectors';
import { clearPassportError, triggerPassportSubscription } from '../../../../../state/ducks/Passport/Passport-Action';
import { getPassportItem, getPassportError, getPassportData } from '../../../../../state/ducks/Passport/Passport-Selectors';
import { getMemberProfile } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';
import { trackPage } from '../../../../helpers/tracking/common/commonTrackingHelpers';
import GraphqlSeoHeadTags from '../../../../components/GraphqlComponents/GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import { getIsPassportUserLoggedIn } from '../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getAccessTokenSafely, auth0 } from '../../../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';
import { getTokenLocalStorage, setTokenLocalStorage } from '../../../../../state/ducks/Member/ducks/Auth/helper/helper';
import { useAuth } from '../../../../../state/ducks/Member/ducks/Auth/Plugin/auth';

const Passport = ({
    userProfile,
    userProfile: {
        email, passportStartDate, passportEndDate, passportFlag,
    },
    featureFlags,
    passportInCart,
    triggerSubscription,
    passportError,
    callClearPassportErrors,
    isPassportUserLoggedIn,
    passportData,
    passportOrderItem,
}) => {
    const history = useHistory();
    const { loginByRedirect } = useAuth();

    const silentAuthEnabled = featureFlags['is-account-passport-silent-auth-enabled'];

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
        }
        if (passportError) callClearPassportErrors();
        const checkPassportMetadata = async () => {
            await getAccessTokenSafely();
            const userInfo = await auth0.getUserInfo();
            if ((!userInfo?.XAtt3 || !userInfo?.XAtt4 || !userInfo?.XAtt5)) {
                history.push(passportData?.myaccount_passport?.non_passport_user?.url || '/');
            }
        };
        /**
         * Handles getting new metadata if passport was
         * recently purchased.
         */
        const refreshMetadata = JSON.parse(getTokenLocalStorage('refreshMetadata'));
        // Confirm it's a boolean to avoid infinite redirects
        if (typeof refreshMetadata === 'boolean' && refreshMetadata && silentAuthEnabled) {
            setTokenLocalStorage(false, 'refreshMetadata');
            loginByRedirect({ prompt: 'none' });
        } else checkPassportMetadata();
    }, []);

    if (!email || !passportStartDate || !passportEndDate || !passportFlag) return null;
    const userDetails = {};
    let daysDifference;
    // checking if user is canceled passport member, if passportFlag is "01-03"
    const isCanceledMembership = passportFlag.includes('01-03');
    const passportPrice = passportOrderItem?.[0]?.cartItems?.[0]?.product?.salePrice;
    try {
        userDetails.email = email;
        const XAtt4ToYYYYMMDD = convertToYYYYMMDD(passportStartDate);
        const XAtt5ToYYYYMMDD = convertToYYYYMMDD(passportEndDate);
        userDetails.startDate = dateMMMMDDYYYY(XAtt4ToYYYYMMDD);
        userDetails.endDate = dateMMMMDDYYYY(XAtt5ToYYYYMMDD);
        const startDateObj = new Date(XAtt4ToYYYYMMDD);
        const endDateObj = new Date(XAtt5ToYYYYMMDD);
        userDetails.memberSince = startDateObj.getFullYear();
        userDetails.renewalDate = dateMMMMDDYYYY(endDateObj, 0, 0, 1);
        userDetails.isActive = new Date().getTime() < endDateObj.getTime();

        const today = new Date().getTime();
        // passport expiration date
        const expirationDate = new Date(passportEndDate);
        // calculating time difference from today and expiration date
        const timeDifference = today - expirationDate;
        // calculating days difference from today and expiration date
        daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
        userDetails.showRenewalBtn = true;
        if (!featureFlags['is-passport-renew-early-on']) {
            userDetails.showRenewalBtn = false;
        }
        // calculating the new end date
        const newEndDate = (today > expirationDate || isCanceledMembership)
            ? dateMMMMDDYYYY(new Date(new Date().setFullYear(new Date().getFullYear() + 1)))
            : dateMMMMDDYYYY(new Date(expirationDate.setFullYear(expirationDate.getFullYear() + 1)));

        const customerServiceMsg = 'Contact our customer service at 1-800-716-4851 to renew your membership';
        if (featureFlags['is-passport-account-expired-messaging-showing']) {
            if (passportEndDate && passportStartDate && daysDifference >= 0) {
                userDetails.message = 'Your passport membership has expired.';
            }
            if (passportEndDate && passportStartDate && daysDifference < 0 && daysDifference >= -90) {
                userDetails.message = `Your passport membership will expire in ${Math.abs(daysDifference)} day${daysDifference === -1 ? '' : 's'}. ${daysDifference >= -2 ? customerServiceMsg : ''}`;
                // hide renewal early btn if expiration date is in 2 days
                if (daysDifference >= -2) userDetails.showRenewalBtn = false;
            }
            // hide renew early btn if end date is greater than 90 days
            if (passportEndDate && passportStartDate && daysDifference < 0 && daysDifference < -90) {
                userDetails.showRenewalBtn = false;
            }
        }
        if (passportInCart.length > 0 && !isPassportUserLoggedIn) {
            userDetails.message = `Celebrations Passport® has been added to your cart for $${passportPrice || passportData?.price}. Your new expiration date will be ${newEndDate} when you complete checkout. Automatically Renews at $29.99/year, Cancel Anytime.`;
            userDetails.successMsg = true;
        } else if (passportError) userDetails.message = passportError;
    } catch (ex) {
        return (
            <>
                <h1>
                    <BackButton />
                    Passport
                </h1>
                <div>
                    Sorry! The data is not available at this time. Please try again
                    later.
                </div>
            </>
        );
    }

    const handleSubscriptionRenewal = (passport_item_sku) => {
        if (triggerPassportSubscription) triggerSubscription(passport_item_sku);
    };

    const handleCheckout = () => {
        if (passportInCart?.[0]?.ordersId) {
            history.push(`/checkout/cart/${passportInCart[0].ordersId}`);
        }
    };

    return (
        <>
            <GraphqlSeoHeadTags seoData={{
                page_title: 'My Account',
                location: { pathname: '/account/passport' },
            }}
            />
            <ComponentPassport
                history={history}
                featureFlags={featureFlags}
                handleSubscriptionRenewal={handleSubscriptionRenewal}
                userProfile={userProfile}
                userDetails={userDetails}
                passportInCart={passportInCart}
                handleCheckout={() => handleCheckout()}
                daysDifference={daysDifference}
            />
        </>
    );
};

Passport.defaultProps = {
    userProfile: {},
    featureFlags: {},
    passportData: {},
    passportInCart: [],
    passportError: '',
    isPassportUserLoggedIn: false,
};

Passport.propTypes = {
    userProfile: shape({
        email: string,
        passportStartDate: string,
        passportEndDate: string,
    }),
    triggerSubscription: func.isRequired,
    featureFlags: object,
    passportData: shape({
        myaccount_passport: object,
    }),
    passportInCart: arrayOf(shape({
        cartItems: arrayOf(shape({
            ordersId: number,
        })),
    })),
    passportError: string,
    callClearPassportErrors: func.isRequired,
    isPassportUserLoggedIn: bool,
    passportOrderItem: object.isRequired,
};

const mapStateToProps = (state) => {
    /* istanbul ignore next */
    let featureFlags = {};
    const passportData = getPassportData(state);
    if (state.app) {
        try {
            featureFlags = {
                ...getFeatureFlags(state),
            };
            // eslint-disable-next-line no-empty
        } catch (ex) { }
    }
    return {
        userProfile: getMemberProfile(state),
        passportInCart: getPassportItem(state),
        passportError: getPassportError(state),
        isPassportUserLoggedIn: getIsPassportUserLoggedIn(state),
        passportData,
        featureFlags,
        passportOrderItem: getPassportItem(state),
    };
};

const mapDispatchToProps = (dispatch) => ({
    triggerSubscription: (data) => dispatch(triggerPassportSubscription('passportPage', data)), // page_on: passportPage, so we can retrieve cart to check gift_card
    callClearPassportErrors: bindActionCreators(clearPassportError, dispatch), // clear passport error
});

const enhance = compose(
    connect(
        mapStateToProps,
        mapDispatchToProps,
    ),
    trackPage({
        type: 'accountpassport',
        name: 'MyAccount',
        accountPage: true,
    }),
    withRouter,
    withPageView({
        pageViewSuccessTest: () => true,
    }),
);
export default enhance(Passport);
